// Create + edit body for a tenant identity member. Rendered inside a
// Modal by IdentityRosterPanel. Mirrors AccountCreateForm; adds typed
// backend-error mapping: invalid_username/invalid_email land under the
// field; everything else (409 uniqueness, 422 null_field) lands in the
// FormShell banner.

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { identityMembers } from "../../api/endpoints/identityMembers";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import { FormShell } from "../../components/forms/FormShell";
import { SelectField } from "../../components/forms/SelectField";
import { TextField } from "../../components/forms/TextField";
import {
  IDENTITY_ROLES,
  type IdentityMemberCreateIn,
  type IdentityMemberOut,
  type IdentityRole,
} from "../../api/types/identityMember";

interface IdentityMemberFormProps {
  tenantSlug: string;
  member?: IdentityMemberOut; // present → edit mode
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

// Codes that belong under a specific field. Everything else — the 409
// uniqueness errors, and the 422 null_field (which the backend raises
// without a `field`, and which this form can't even trigger since
// `isValid` blocks empty submits) — falls through to the FormShell banner.
const FIELD_CODE: Record<string, keyof IdentityMemberCreateIn> = {
  invalid_username: "username",
  invalid_email: "email",
};

function fieldError(
  error: unknown,
  field: keyof IdentityMemberCreateIn,
): string | null {
  if (!(error instanceof ApiError)) return null;
  // ApiError carries the human detail on `.message` (passed to super());
  // there is no `.detail` property — see api/errors.ts.
  return FIELD_CODE[error.code] === field ? error.message : null;
}

function isFieldError(error: unknown): boolean {
  return error instanceof ApiError && error.code in FIELD_CODE;
}

export function IdentityMemberForm({
  tenantSlug,
  member,
  onDone,
  onPendingChange,
}: IdentityMemberFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<IdentityMemberCreateIn>({
    username: member?.username ?? "",
    email: member?.email ?? "",
    first_name: member?.first_name ?? "",
    last_name: member?.last_name ?? "",
    role: member?.role ?? "tenant_admin",
  });

  const mutation = useMutation({
    mutationFn: () =>
      member
        ? identityMembers.update(tenantSlug, member.id, values)
        : identityMembers.create(tenantSlug, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.identityMembers(tenantSlug) });
      onDone();
    },
  });

  // Surface in-flight state so the wrapping Modal can lock close while a
  // create/save is landing (mirrors AccountCreateForm).
  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid =
    values.username !== "" &&
    values.email !== "" &&
    values.first_name !== "" &&
    values.last_name !== "";

  // Field-coded errors render under the field; suppress the FormShell
  // banner for those so the message isn't duplicated.
  const bannerError = isFieldError(mutation.error) ? undefined : mutation.error;

  return (
    <FormShell
      submitLabel={member ? "Save member" : "Add member"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      submitDisabled={!isValid}
      error={bannerError}
      errorTitle={member ? "Save member failed" : "Add member failed"}
    >
      <TextField
        label="Username"
        value={values.username}
        onChange={(username) => setValues((v) => ({ ...v, username }))}
        required
        placeholder="fatima.niazi"
        helper="Login id; a-z 0-9 . _ - (editable later)."
        error={fieldError(mutation.error, "username")}
      />
      <TextField
        label="Email"
        type="email"
        value={values.email}
        onChange={(email) => setValues((v) => ({ ...v, email }))}
        required
        placeholder="fatima@example.org"
        error={fieldError(mutation.error, "email")}
      />
      <TextField
        label="First name"
        value={values.first_name}
        onChange={(first_name) => setValues((v) => ({ ...v, first_name }))}
        required
      />
      <TextField
        label="Last name"
        value={values.last_name}
        onChange={(last_name) => setValues((v) => ({ ...v, last_name }))}
        required
      />
      <SelectField
        label="Role"
        value={values.role}
        onChange={(role) => setValues((v) => ({ ...v, role: role as IdentityRole }))}
        required
        options={IDENTITY_ROLES}
        helper="tenant_admin manages the tenant; tenant_user is a regular member."
      />
    </FormShell>
  );
}
